import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "../../../Components/Buttons";
import { FormImage, FormInput, FormSection, FormSelect } from "../../../Components/Form";
import RichTextEditor from "../../../Components/RichTextEditor";
import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { blog_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import { useFetchBlogCategoriesAllQuery } from "../../../stores/api/blogCategories.api";
import {
  useCreateBlogMutation,
  useUpdateBlogMutation,
} from "../../../stores/api/blogs.api";
import { buildFormData } from "../../../stores/api/helpers/payload.helper";

const STATUS_OPTIONS = [
  { value: "0", label: "Draft" },
  { value: "1", label: "Published" },
  { value: "2", label: "Archived" },
];

const EMPTY = {
  category_id: "",
  title: "",
  slug: "",
  short_description: "",
  content: "",
  image: null,
  image_alt: "",
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  status: "1",
  author: "",
};

const toSlug = (str) => str.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const CreateBlog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const details = location?.state?.blog;

  const { data: categoriesRaw = [] } = useFetchBlogCategoriesAllQuery();
  const categories = useMemo(
    () => (categoriesRaw || []).map((c) => ({ value: String(c.id), label: c.name })),
    [categoriesRaw]
  );

  const [createBlog, { isLoading: creating }] = useCreateBlogMutation();
  const [updateBlog, { isLoading: updating }] = useUpdateBlogMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    ...EMPTY,
    ...(details
      ? {
          category_id: details.category_id ? String(details.category_id) : "",
          title: details.title || "",
          slug: details.slug || "",
          short_description: details.short_description || "",
          content: details.content || "",
          image_alt: details.image_alt || "",
          meta_title: details.meta_title || "",
          meta_description: details.meta_description || "",
          meta_keywords: details.meta_keywords || "",
          status: details.status ? String(details.status) : "1",
          author: details.author || "",
        }
      : {}),
  });

  const setField = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "title" && !details) next.slug = toSlug(value);
      return next;
    });
  };

  const resetForm = () => setFormData(EMPTY);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = buildFormData(formData);

      if (details) {
        await updateBlog({ id: details.id, body }).unwrap();
      } else {
        await createBlog(body).unwrap();
      }

      ToastMessage({
        type: "success",
        title: details ? "Blog updated" : "Blog created",
        message: details ? "Blog updated successfully!" : "Blog post created successfully!",
      });
      if (!details) resetForm();
      navigate(ROUTE_PATHS.ADMIN_BLOG);
    } catch (err) {
      handleError(err, "Something went wrong.");
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb title={details ? "Edit Blog" : "Create Blog"} />

      <form onSubmit={onSubmit} className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Blog Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSelect label="Category" required options={categories} value={formData.category_id} onChange={(val) => setField("category_id", val ?? "")} />
            <FormInput label="Title" name="title" value={formData.title} onChange={(val) => setField("title", val)} placeholder="Enter blog title" />
            <FormInput label="Slug" name="slug" value={formData.slug} onChange={(val) => setField("slug", val)} placeholder="auto-generated-from-title" />
            <FormInput label="Author" name="author" value={formData.author} isRequired onChange={(val) => setField("author", val)} placeholder="Author name" />
            <FormInput label="Short Description" name="short_description" value={formData.short_description} onChange={(val) => setField("short_description", val)} placeholder="Brief summary" rows={2} className="md:col-span-2" />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Content <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(html) => setField("content", html)}
                placeholder="Enter blog content"
                minHeight={300}
              />
            </div>
          </div>
        </FormSection>

        <FormSection title="Blog Image">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormImage
              label="Image"
              hint="Recommended aspect ratio: 5:2 (e.g. 500×200px)"
              required
              initialPreviewUrl={details?.image ? (blog_image_url ?? "") + details.image : null}
              onChange={(file) => setFormData((prev) => ({ ...prev, image: file }))}
            />
            <FormInput label="Image Alt Text" name="image_alt" value={formData.image_alt} onChange={(val) => setField("image_alt", val)} placeholder="Describe the image" />
          </div>
        </FormSection>

        <FormSection title="SEO / Meta">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Meta Title" name="meta_title" value={formData.meta_title} onChange={(val) => setField("meta_title", val)} placeholder="SEO title" />
            <FormInput label="Meta Keywords" name="meta_keywords" value={formData.meta_keywords} onChange={(val) => setField("meta_keywords", val)} placeholder="keyword1, keyword2" />
            <FormInput label="Meta Description" name="meta_description" value={formData.meta_description} onChange={(val) => setField("meta_description", val)} placeholder="SEO description" rows={3} className="md:col-span-2" />
          </div>
        </FormSection>

        <FormSection title="Status">
          <div className="max-w-xs">
            <FormSelect label="Status" required options={STATUS_OPTIONS} value={formData.status} onChange={(val) => setField("status", val ?? "1")} />
          </div>
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" onClick={() => { resetForm(); navigate(ROUTE_PATHS.ADMIN_BLOG); }} className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200" disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" className="!bg-orange-500 !text-white hover:!bg-orange-600" loading={loading} disabled={loading} loadingText={details ? "Updating..." : "Creating..."}>
            {details ? "Update Blog" : "Create Blog"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateBlog;
